import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeColors, themeUtils } from '../utils/theme';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  colors: ThemeColors;
  getThemeClass: (lightClass: string, darkClass: string) => string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }

    // Fall back to system preference (with safe check for testing environment)
    if (typeof window !== 'undefined' && window.matchMedia) {
      try {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        return mediaQuery?.matches || false;
      } catch (error) {
        // Fallback if matchMedia fails
        return false;
      }
    }

    // Default to light mode if matchMedia is not available
    return false;
  });

  useEffect(() => {
    // Apply theme to document
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Persist theme preference
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    // Only set up listener if matchMedia is available
    if (typeof window !== 'undefined' && window.matchMedia) {
      try {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        if (mediaQuery && typeof mediaQuery.addEventListener === 'function') {
          const handleChange = (e: MediaQueryListEvent) => {
            // Only update if no saved preference exists
            const savedTheme = localStorage.getItem('theme');
            if (!savedTheme) {
              setIsDarkMode(e.matches);
            }
          };

          mediaQuery.addEventListener('change', handleChange);
          return () => mediaQuery.removeEventListener('change', handleChange);
        }
      } catch (error) {
        // Silently fail if matchMedia setup fails
      }
    }
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const colors = themeUtils.getColors(isDarkMode);

  const getThemeClass = (lightClass: string, darkClass: string) => {
    return themeUtils.getThemeClass(isDarkMode, lightClass, darkClass);
  };

  const value: ThemeContextType = {
    isDarkMode,
    toggleDarkMode,
    colors,
    getThemeClass,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};